import { createSlice } from "@reduxjs/toolkit";

/**
 * `auth` slice — user identity, role/permission flags, subscription
 * info and a couple of UI-state flags (profile modal). Populated by
 * `userSettings` after `/me` succeeds; reset by `cleanupUserSettings`
 * on logout.
 *
 * Audit notes addressed in this revision:
 *   - Every field that `userSettings` writes is now declared in
 *     `initialState` so consumers can `state.auth.field` safely
 *     pre-login without optional-chaining tripping. Previously
 *     `linked_clinics`, `active_clinic`, `email`, `phone`,
 *     `consent_form` popped into existence at login — the SideBar
 *     `linked_clinics.map(...)` would crash on a fresh render.
 *   - `cleanupUserSettings` now resets EVERY field (was only resetting
 *     about half). The app-wide `rootReducer` reset on the action was
 *     masking this gap; relying on that shortcut is fragile.
 *   - `variant` initialState was the literal string "null" — replaced
 *     with the empty string so `.startsWith("Pro")` semantics are
 *     correct (false rather than "Pro feature gated by an undefined
 *     non-string default").
 */

const initialState = {
  loggedIn: false,
  token: null,
  userId: null,
  displayName: null,
  companyId: null,
  role: null,
  email: null,
  phone: null,
  country: "",
  isOpen: false,
  subscriptionInfo: "",
  treatment_qty: false,
  whatsapp_credit_balance: "0",
  sms_credit_balance: "0",
  // Empty string (not the string "null") so `.startsWith("Pro")`
  // returns false instead of hitting an unexpected match.
  variant: "",
  linked_clinics: [],
  active_clinic: null,
  subscription_started_at: "-",
  subscription_expires_at: "-",
  subscription_expires_iso: null,
  demo_end_at: null,
  whatsapp_credit_enabled: true,
  sms_credit_enabled: true,
  // Free trial over and no plan bought: sign-in works, but only to buy a plan.
  restricted: false,
  // Low-credit notices due now, worked out by the server (once-a-day rules).
  credit_alerts: [],
  // Renewal reminder due now ({ days, bucket, expiresAt }), or null.
  subscription_alert: null,
  // Plan lapsed but still inside the grace period: full access, with warnings.
  in_grace: false,
  grace_ends_on: null,
  access_reason: null, // "trial" | "subscription"
  account_manager_name: "",
  account_manager_phone: "",
  plan_name: "Free Trial",
  birthdayWish: false,
  labOrderArrivalUpdate: false,
  financeAbstract: false,
  consent_form: false,
};

export const authSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    userSettings: (state, action) => {
      state.loggedIn = true;
      state.token = action.payload?.token;
      localStorage.setItem("token", action.payload.token);
      state.userId = action.payload?._id;
      state.displayName = action.payload?.name;
      state.companyId = action.payload?.company_id;
      state.country = action.payload?.country;
      state.role = action.payload?.role1;
      state.email = action.payload?.emailId;
      state.phone = action.payload?.phoneNumber;
      state.subscriptionInfo = action.payload?.subscriptionInfo;
      state.treatment_qty = action.payload?.treatment_qty;
      state.whatsapp_credit_balance = action.payload?.whatsapp_credit_balance;
      state.sms_credit_balance = action.payload?.sms_credit_balance;
      state.whatsapp_credit_enabled =
        action.payload?.whatsapp_credit_enabled ?? true;
      state.sms_credit_enabled = action.payload?.sms_credit_enabled ?? true;
      state.restricted = !!action.payload?.restricted;
      state.credit_alerts = action.payload?.credit_alerts ?? [];
      state.subscription_alert = action.payload?.subscription_alert ?? null;
      state.in_grace = !!action.payload?.in_grace;
      state.grace_ends_on = action.payload?.grace_ends_on ?? null;
      state.access_reason = action.payload?.access_reason ?? null;
      state.account_manager_name = action.payload?.account_manager_name ?? "";
      state.account_manager_phone = action.payload?.account_manager_phone ?? "";
      state.variant = action.payload?.variant ?? "";
      // Defensive: `linked_clinics` may be missing from the /me
      // payload on legacy users; default to [] so .map doesn't crash.
      state.linked_clinics = action.payload?.linked_clinics ?? [];
      state.active_clinic = action.payload?.active_clinic ?? null;
      state.subscription_started_at = action.payload?.subscription_started_at;
      state.subscription_expires_at = action.payload?.subscription_expires_at;
      state.subscription_expires_iso =
        action.payload?.subscription_expires_iso ?? null;
      state.demo_end_at = action.payload?.demo_end_at ?? null;
      state.plan_name = action.payload?.plan_name;
      state.birthdayWish = action.payload?.birthdayWish;
      state.labOrderArrivalUpdate = action.payload?.labOrderArrivalUpdate;
      state.financeAbstract = action.payload?.financeAbstract;
      state.consent_form = action.payload?.consent_form;
    },
    cleanupUserSettings: (state) => {
      // Reset every field this slice owns, not just half of them.
      // The rootReducer also performs a global state reset on this
      // action — keeping THIS reducer thorough means it stays correct
      // even if anyone later removes that shortcut.
      localStorage.removeItem("token");
      Object.assign(state, initialState);
    },
    openProfileModal: (state) => {
      state.isOpen = true;
    },
    closeProfileModal: (state) => {
      state.isOpen = false;
    },
    // After a renewal: the clinic's new plan dates and balances.
    updateSubscriptionState: (state, action) => {
      const p = action.payload || {};
      if (p.subscription_started_at !== undefined)
        state.subscription_started_at = p.subscription_started_at;
      if (p.subscription_expires_at !== undefined)
        state.subscription_expires_at = p.subscription_expires_at;
      if (p.subscription_expires_iso !== undefined)
        state.subscription_expires_iso = p.subscription_expires_iso;
      if (p.plan_name !== undefined) state.plan_name = p.plan_name;
      if (p.whatsapp_credit_balance !== undefined)
        state.whatsapp_credit_balance = p.whatsapp_credit_balance;
      if (p.sms_credit_balance !== undefined)
        state.sms_credit_balance = p.sms_credit_balance;
    },
    updateTreatmentQtyState: (state, action) => {
      state.treatment_qty = action.payload;
    },
  },
});

export const {
  userSettings,
  cleanupUserSettings,
  openProfileModal,
  closeProfileModal,
  updateSubscriptionState,
  updateTreatmentQtyState,
} = authSlice.actions;

export default authSlice.reducer;
